import { useLanguageStore } from "../../store/languageStore";
import { useEffect, useRef, useState } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { LottieRefCurrentProps } from "lottie-react";
import ImageButton from "../button/image-button";
import { useNavigate } from "react-router-dom";
import LottieVoice from "../lottie/lottie-voice";
import { useFindPath } from "../../hooks/useFindPath";
import useMdBreakpoint from "../../hooks/useMdBreakpoint";
import TextToSpeech from "../text/text-speech";
import { useTranslation } from "react-i18next";
import WarningText from "../text/warning-text";
import { useAppStore } from "../../store/appStore";
import React from "react";

export const HomeVoiceBase = (props: any) => {
  const { bottomPanel = false, onClose, redirectToNav = false } = props;
  const voiceLanguage = useLanguageStore((state) => state.voiceLanguage);
  const [micOn, setMicOn] = useState(true);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const isMd = useMdBreakpoint();
  const [hasPermission, setHasPermission] = useState(false);
  const micInstruction = useAppStore((state) => state.micInstruction);
  const setMicInstruction = useAppStore((state) => state.setMicInstruction);
  const setShowNavError = useAppStore((state) => state.setShowNavError);
  const lastProcessedRef = useRef("");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleUserQuery } = useFindPath(redirectToNav);
  const {
    listening,
    finalTranscript,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  useEffect(() => {
    checkPermission();
    return () => {
      SpeechRecognition.stopListening();
      resetTranscript();
      lastProcessedRef.current = "";
    };
  }, []);

  useEffect(() => {
    if (!listening) return;

    if (finalTranscript === lastProcessedRef.current) return;

    lastProcessedRef.current = finalTranscript;
    console.log(`Final transcript: ${finalTranscript}`);

    findNavigation(finalTranscript);
  }, [finalTranscript]);

  function getBrowserInstruction() {
    const userAgent = navigator.userAgent;
    console.log("userAgent", userAgent);
    if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Safari") > -1) {
      return "mic_permission_chrome";
    } else if (userAgent.indexOf("Safari") > -1) {
      if (userAgent.includes("Mobile")) {
        return "mic_permission_safari_mobile";
      } else {
        return "mic_permission_safari_desktop";
      }
    } else return "mic_permission_generic";
  }

  const checkPermission = async () => {
    try {
      const result = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (result.active) {
        setHasPermission(true);
        setShowNavError(false);
      }
      console.log("result", result);
    } catch (e) {
      setMicInstruction(getBrowserInstruction());
      setHasPermission(false);
      setShowNavError(true); // display instruction in nav header for mobile voice
      console.log("permission denied", e);
    }
  };

  const startListening = () => {
    console.log("start listening");
    SpeechRecognition.startListening({ language: voiceLanguage });
  };

  const findNavigation = async (transcript: string) => {
    await handleUserQuery(transcript);
  };

  const muteMicBtn = (size: number) => {
    return (
      <ImageButton
        onClick={() => {
          setMicOn(false);
          lottieRef.current?.goToAndStop(5000);
          SpeechRecognition.stopListening();
        }}
        src="/assets/icon_mic_on.png"
        alt="microphone icon"
        size={size}
      />
    );
  };

  const unmuteMicBtn = (size: number) => {
    return (
      <ImageButton
        onClick={() => {
          SpeechRecognition.startListening();
          setMicOn(true);
          lottieRef.current?.goToAndPlay(0);
        }}
        src="/assets/icon_mic_off.png"
        alt="microphone icon"
        size={size}
      />
    );
  };

  const closeButton = (size: number) => {
    return (
      <ImageButton
        onClick={() => {
          SpeechRecognition.stopListening();
          if (onClose) {
            onClose();
          } else {
            navigate(-1);
          }
        }}
        src="/assets/icon_btn_close.png"
        alt="microphone icon"
        size={size}
      />
    );
  };

  const MobileVoiceChat = () => {
    return (
      <div className="flex h-full">
        <div className="flex flex-1 items-end justify-start">
          {micOn ? muteMicBtn(40) : unmuteMicBtn(40)}
        </div>
        <div className="flex flex-1 items-center justify-center">
          <LottieVoice listening={listening} lottieRef={lottieRef} />
        </div>
        <div className="flex flex-1 items-end justify-end">
          {closeButton(40)}
        </div>
      </div>
    );
  };

  const DesktopVoiceChat = () => {
    return (
      <div className="flex h-full w-full justify-center">
        <div className="flex w-full flex-col items-center justify-center">
          <div className="w-40 flex justify-center">
            <LottieVoice listening={listening} lottieRef={lottieRef} />
          </div>
          <div className="w-full mt-auto flex justify-between z-10">
            {micOn ? muteMicBtn(40) : unmuteMicBtn(40)}
            {closeButton(40)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <TextToSpeech
        text={t("landing")}
        showText={false}
        onFinish={() => startListening()}
      />
      {!hasPermission && !bottomPanel && (
        <WarningText
          title={t("grant_mic_permission")}
          description={t(micInstruction)}
        />
      )}
      {browserSupportsSpeechRecognition ? (
        bottomPanel ? (
          <>
            {!isMd && (
              <div className="md:hidden">
                <MobileVoiceChat />
              </div>
            )}
            <div className="hidden md:flex md:h-full">
              <DesktopVoiceChat />
            </div>
          </>
        ) : (
          <div className="flex flex-1 p-4 md:items-center md:justify-center">
            <div
              className={`flex w-full flex-col md:h-[490px] md:w-[393px] md:rounded-2xl md:shadow-lg md:overflow-hidden ${
                isMd ? "bg-voice" : ""
              }`}
            >
              <div className="flex-1 overflow-auto p-8">
                <LottieVoice
                  listening={hasPermission && listening}
                  lottieRef={lottieRef}
                />
              </div>
              <div className="mt-auto flex justify-between p-8">
                {micOn ? muteMicBtn(60) : unmuteMicBtn(60)}
                {closeButton(60)}
              </div>
            </div>
          </div>
        )
      ) : (
        <span>Browser doesn't support speech recognition.</span>
      )}
    </>
  );
};

export const HomeVoice = React.memo(HomeVoiceBase);
